// Limity klockow per trasa: mapa moze ograniczac, ile czego wolno zbudowac.
//
// Dwie osie:
//  - limit po KATEGORII, np. { wheel: 2 } = najwyzej dwa kola lacznie,
//  - limit po TYPIE, np. { wheel_small: 0 } = zakaz malych kol.
// Limit typu ma pierwszenstwo nad limitem kategorii.
//
// Czysta logika (bez DOM i bez Plancka), wiec latwo ja testowac. Uzywaja jej:
// walidacja (komunikat PART_LIMIT), magazyn konstrukcji (twarda blokada
// dokladania) i katalog w UI (licznik "ile jeszcze wolno").

import { getDefinition, hasDefinition } from './catalog';
import type { PartCategory, PartDefinition, RobotBlueprint } from './types';

/** Nazwy kategorii klockow - wspolne dla grup w katalogu i komunikatow. */
export const CATEGORY_LABELS: Record<PartCategory, string> = {
  frame: 'Rama i ciezar',
  mount: 'Osie',
  wheel: 'Kola',
  drive: 'Naped',
  gear: 'Przekladnie',
  battery: 'Zasilanie',
};

/** Ograniczenia klockow dla trasy (opcjonalne). */
export interface PartLimits {
  /** Limit liczby klockow w kategorii, np. { wheel: 2 } = najwyzej dwa kola. */
  categories?: Partial<Record<PartCategory, number>>;
  /** Limit liczby klockow danego typu; nadpisuje limit kategorii (0 = zakaz). */
  types?: Record<string, number>;
}

/** Ile egzemplarzy danego typu jest w konstrukcji. */
export function countType(blueprint: RobotBlueprint, typeId: string): number {
  return blueprint.parts.reduce((sum, part) => (part.typeId === typeId ? sum + 1 : sum), 0);
}

/** Ile egzemplarzy klockow z danej kategorii jest w konstrukcji. */
export function countCategory(blueprint: RobotBlueprint, category: PartCategory): number {
  return blueprint.parts.reduce((sum, part) => {
    if (!hasDefinition(part.typeId)) return sum;
    return getDefinition(part.typeId).category === category ? sum + 1 : sum;
  }, 0);
}

interface LimitRule {
  max: number;
  count(blueprint: RobotBlueprint): number;
}

/** Regula limitu dla klocka: najpierw typ, potem kategoria (undefined = bez limitu). */
function ruleFor(def: PartDefinition, limits: PartLimits | undefined): LimitRule | undefined {
  if (!limits) return undefined;

  const byType = limits.types?.[def.typeId];
  if (byType !== undefined) {
    return { max: byType, count: (blueprint) => countType(blueprint, def.typeId) };
  }

  const byCategory = limits.categories?.[def.category];
  if (byCategory !== undefined) {
    return { max: byCategory, count: (blueprint) => countCategory(blueprint, def.category) };
  }

  return undefined;
}

/** Maksymalna liczba egzemplarzy klocka na trasie (undefined = bez limitu). */
export function partLimit(def: PartDefinition, limits: PartLimits | undefined): number | undefined {
  return ruleFor(def, limits)?.max;
}

/** Ile jeszcze wolno dodac klockow tego typu (undefined = bez limitu). */
export function remainingFor(
  def: PartDefinition,
  blueprint: RobotBlueprint,
  limits: PartLimits | undefined,
): number | undefined {
  const rule = ruleFor(def, limits);
  if (!rule) return undefined;
  return rule.max - rule.count(blueprint);
}

/** Przekroczony limit jednej reguly (typu albo kategorii). */
export interface LimitBreach {
  /** Klucz reguly ("type:..." albo "cat:...") - do deduplikacji komunikatow. */
  key: string;
  /** Limit, ktory zostal przekroczony. */
  max: number;
  /** Ile egzemplarzy jest w konstrukcji. */
  used: number;
  /** Czytelna nazwa tego, czego dotyczy limit (np. "Kola", "Kolo male"). */
  label: string;
  /** Identyfikatory klockow, ktorych dotyczy regula. */
  partIds: string[];
}

/** Wszystkie przekroczone limity konstrukcji (pusta lista = OK). */
export function limitBreaches(
  blueprint: RobotBlueprint,
  limits: PartLimits | undefined,
): LimitBreach[] {
  if (!limits) return [];

  const groups = new Map<string, { max: number; label: string; partIds: string[] }>();
  for (const part of blueprint.parts) {
    if (!hasDefinition(part.typeId)) continue;
    const def = getDefinition(part.typeId);

    const byType = limits.types?.[def.typeId];
    const byCategory = limits.categories?.[def.category];
    const key =
      byType !== undefined
        ? `type:${def.typeId}`
        : byCategory !== undefined
          ? `cat:${def.category}`
          : null;
    if (key === null) continue;

    const group = groups.get(key) ?? {
      max: byType ?? byCategory ?? 0,
      label: byType !== undefined ? def.label : CATEGORY_LABELS[def.category],
      partIds: [],
    };
    group.partIds.push(part.id);
    groups.set(key, group);
  }

  const breaches: LimitBreach[] = [];
  for (const [key, group] of groups) {
    if (group.partIds.length > group.max) {
      breaches.push({
        key,
        max: group.max,
        used: group.partIds.length,
        label: group.label,
        partIds: group.partIds,
      });
    }
  }
  return breaches;
}
